export function productKey(categoryIndex, productIndex) {
  return `${categoryIndex}:${productIndex}`;
}

export function buildCollisionMap(preview) {
  return new Map(
    (preview?.collisions || []).map((collision) => [collision.productKey, collision]),
  );
}

export function filterDraftForCommit(draft, excludedCategories = {}, excludedProducts = {}) {
  if (!draft) return null;

  return {
    ...draft,
    categories: (draft.categories || [])
      .map((category, categoryIndex) => ({ category, categoryIndex }))
      .filter(({ categoryIndex }) => !excludedCategories[categoryIndex])
      .map(({ category, categoryIndex }) => ({
        ...category,
        products: (category.products || []).filter(
          (_, productIndex) => !excludedProducts[productKey(categoryIndex, productIndex)],
        ),
      }))
      .filter((category) => category.products.length > 0),
  };
}

export function buildCommitProductKeyMap(draft, excludedCategories = {}, excludedProducts = {}) {
  const map = new Map();
  let nextCategoryIndex = 0;

  (draft?.categories || []).forEach((category, categoryIndex) => {
    if (excludedCategories[categoryIndex]) return;
    let nextProductIndex = 0;
    (category.products || []).forEach((_, productIndex) => {
      const originalKey = productKey(categoryIndex, productIndex);
      if (excludedProducts[originalKey]) return;
      map.set(originalKey, productKey(nextCategoryIndex, nextProductIndex));
      nextProductIndex += 1;
    });
    if (nextProductIndex > 0) nextCategoryIndex += 1;
  });

  return map;
}

export function buildDecisionPayload(
  preview,
  decisions = {},
  excludedCategories = {},
  excludedProducts = {},
  draft = null,
) {
  const commitKeyMap = buildCommitProductKeyMap(
    draft,
    excludedCategories,
    excludedProducts,
  );
  return (preview?.collisions || [])
    .filter(
      (collision) =>
        !excludedCategories[collision.categoryIndex] &&
        !excludedProducts[collision.productKey] &&
        decisions[collision.productKey],
    )
    .map((collision) => ({
      productName: collision.productName,
      productKey: commitKeyMap.get(collision.productKey) || collision.productKey,
      decision: decisions[collision.productKey],
    }));
}

export function summarizeReview(draft, preview, decisions = {}, excludedCategories = {}, excludedProducts = {}) {
  const collisionMap = buildCollisionMap(preview);
  const summary = {
    create: 0,
    replace: 0,
    ignore: 0,
    excluded: 0,
    undecided: 0,
  };

  (draft?.categories || []).forEach((category, categoryIndex) => {
    (category.products || []).forEach((_, productIndex) => {
      const key = productKey(categoryIndex, productIndex);
      if (excludedCategories[categoryIndex] || excludedProducts[key]) {
        summary.excluded += 1;
        return;
      }

      const collision = collisionMap.get(key);
      if (!collision) {
        summary.create += 1;
        return;
      }

      const decision = decisions[key];
      if (!decision) summary.undecided += 1;
      else if (decision === 'REPLACE') summary.replace += 1;
      else if (decision === 'CREATE_NEW') summary.create += 1;
      else summary.ignore += 1;
    });
  });

  return summary;
}

export function canCommitReview(summary, filteredDraft) {
  const total = (filteredDraft?.categories || []).reduce(
    (count, category) => count + (category.products || []).length,
    0,
  );
  return total > 0 && summary.undecided === 0;
}
